import type { DirectorySyncStatus } from '@jarvis/platform-ui';
import { apiFetch, readErrorMessage, readJson } from './client';

export interface ClientLicenseSummary {
  tenantId: string;
  tenantDisplayName: string | null;
  p2SkuId: string | null;
  p2SkuPartNumber: string | null;
  prepaidUnits: number;
  consumedUnits: number;
  availableUnits: number;
  usersWithP2: number;
  usersMissingP2: number;
  syncedAt: string | null;
  collectionError: string | null;
  lastJobFinishedAt: string | null;
  lastJobStatus: string | null;
}

export interface ClientLicenseUserMissingP2 {
  id: string;
  userPrincipalName: string;
  displayName: string;
  accountEnabled: boolean;
}

export interface ClientLicenseUsersMissingP2Page {
  total: number;
  items: ClientLicenseUserMissingP2[];
}

function tenantBase(tenantId: string): string {
  return `/api/admin/client-accounts/tenants/${encodeURIComponent(tenantId)}`;
}

export async function fetchClientLicenseSummary(tenantId: string): Promise<ClientLicenseSummary> {
  const res = await apiFetch(`${tenantBase(tenantId)}/license-summary`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientLicenseSummary>(res);
}

export async function fetchClientLicenseSummarySyncStatus(tenantId: string): Promise<DirectorySyncStatus> {
  const res = await apiFetch(`${tenantBase(tenantId)}/license-summary/sync-status`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<DirectorySyncStatus>(res);
}

export async function triggerClientLicenseSummarySync(tenantId: string): Promise<void> {
  const res = await apiFetch(`${tenantBase(tenantId)}/license-summary/sync`, { method: 'POST' });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function listClientLicenseUsersMissingP2(
  tenantId: string,
  opts: { skip?: number; take?: number } = {}
): Promise<ClientLicenseUsersMissingP2Page> {
  const params = new URLSearchParams();
  if (opts.skip != null) params.set('skip', String(opts.skip));
  if (opts.take != null) params.set('take', String(opts.take));
  const qs = params.toString();
  const res = await apiFetch(`${tenantBase(tenantId)}/license-summary/users-missing-p2${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientLicenseUsersMissingP2Page>(res);
}
